import { useEffect, useState } from "react";

// Upper bound so the gate opens even if no LCP entry is ever reported
// (e.g. browsers without the LCP API, or pages with no LCP candidate).
const LCP_FALLBACK_MS = 4000;

/**
 * Returns true once the Largest Contentful Paint has been recorded, so
 * third-party scripts can be kept off the LCP critical path.
 *
 * LCP is considered final on first user input or when the page is hidden;
 * until then the browser may still report a later, larger candidate.
 *
 * Must be used from a Client Component ("use client").
 */
export function useAfterLCP(): boolean {
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof PerformanceObserver === "undefined") {
      setDone(true);
      return;
    }

    let observer: PerformanceObserver | undefined;
    const finish = () => {
      observer?.disconnect();
      clearTimeout(fallbackId);
      setDone(true);
    };
    const fallbackId = setTimeout(finish, LCP_FALLBACK_MS);

    try {
      observer = new PerformanceObserver(() => {
        // First candidate seen; wait for the load event so it has settled.
        if (document.readyState === "complete") finish();
        else window.addEventListener("load", finish, { once: true });
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      finish();
    }

    return () => {
      observer?.disconnect();
      clearTimeout(fallbackId);
      window.removeEventListener("load", finish);
    };
  }, []);

  return done;
}

export default useAfterLCP;
